// VivahMuhuratTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2Divider,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardSlider,
  WizardChips,
  WizardDropdown,
  WizardTextInput,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import { AstriaIndiaV2VivahData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";

const SUBCATEGORY_NAME = "Vivah Muhurat";

const TOTAL_STEPS = 4;

interface PersonFields {
  name: string;
  dob: string;
  time: string;
  place: string;
}

const emptyPerson = (): PersonFields => ({ name: "", dob: "", time: "", place: "" });

interface PersonDetailsCopy {
  detailsSuffix: string;
  namePlaceholder: (person: string) => string;
  dobPlaceholder: string;
  timePlaceholder: string;
  placePlaceholder: string;
}

const PersonDetailsGroup: React.FC<{
  label: string;
  value: PersonFields;
  onChange: (value: PersonFields) => void;
  copy: PersonDetailsCopy;
}> = ({ label, value, onChange, copy }) => (
  <div style={{ marginBottom: 20 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: SHELL_COLORS_INDIA_V2.mutedColor,
        marginBottom: 8,
      }}
    >
      {label}
      {copy.detailsSuffix}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <WizardTextInput
        value={value.name}
        onChange={(v) => onChange({ ...value, name: v })}
        placeholder={copy.namePlaceholder(label)}
      />
      <WizardTextInput
        value={value.dob}
        onChange={(v) => onChange({ ...value, dob: v })}
        placeholder={copy.dobPlaceholder}
      />
      <WizardTextInput
        value={value.time}
        onChange={(v) => onChange({ ...value, time: v })}
        placeholder={copy.timePlaceholder}
      />
      <WizardTextInput
        value={value.place}
        onChange={(v) => onChange({ ...value, place: v })}
        placeholder={copy.placePlaceholder}
      />
    </div>
  </div>
);

const VivahMuhuratTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const [lang, setLang] = useIndiaV2Language();
  const copy = ASTRIA_INDIA_V2_COPY[lang];
  const t = copy.vivah;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } = useAstriaIndiaV2Tab({
    categoryName,
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [step, setStep] = useState(1);
  const [bride, setBride] = useState<PersonFields>(emptyPerson());
  const [groom, setGroom] = useState<PersonFields>(emptyPerson());
  const [yourReadiness, setYourReadiness] = useState(50);
  const [theirReadiness, setTheirReadiness] = useState(50);
  const [rhythm, setRhythm] = useState<string | null>(null);
  const [period, setPeriod] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  // Step 1 collects both Bride and Groom details upfront so a real
  // two-chart timing reading can be computed — the backend
  // (astriaIndiaV2Service.js) is instructed never to ask for these again.
  const step1Valid =
    !!bride.name.trim() && !!bride.dob.trim() && !!groom.name.trim() && !!groom.dob.trim();

  const buildMessage = () => {
    const rhythmLabel = t.rhythmOptions.find((r) => r.value === rhythm)?.label || "Steady";
    const periodLabel =
      t.periodOptions.find((p) => p.value === period)?.label || "next 3 months";

    return `Read my Vivah Muhurat (marriage timing window).
Bride's Name: ${bride.name.trim()}.
Bride's Date of Birth: ${bride.dob.trim()}.
Bride's Time of Birth: ${bride.time.trim() || "Not provided"}.
Bride's Place of Birth: ${bride.place.trim() || "Not provided"}.
Groom's Name: ${groom.name.trim()}.
Groom's Date of Birth: ${groom.dob.trim()}.
Groom's Time of Birth: ${groom.time.trim() || "Not provided"}.
Groom's Place of Birth: ${groom.place.trim() || "Not provided"}.
Bride's readiness: ${yourReadiness}/100. Groom's readiness: ${theirReadiness}/100.
Current rhythm between them: ${rhythmLabel}.
Preferred timing: ${periodLabel}.`;
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const vivahData = message?.astriaIndiaV2Data as AstriaIndiaV2VivahData | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="vivah"
        lang={lang}
        onLangChange={setLang}
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && vivahData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<Clock className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#C9A24B", "#6B3FA0"]}
              shareText={`${t.tabLabel}\n\n${vivahData.opening}\n\n${vivahData.closing}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={vivahData.highlight} />
              {vivahData.opening && (
                <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", marginBottom: 16 }}>
                  {vivahData.opening}
                </p>
              )}
              <AstriaIndiaV2SignatureLine value={vivahData.micro_imagery} />
              <AstriaIndiaV2FieldBlock
                label={t.fields.compatibilitySnapshot}
                value={vivahData.compatibility_snapshot}
              />

              {vivahData.recommended_windows?.length > 0 && (
                <>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: SHELL_COLORS_INDIA_V2.accent,
                      marginBottom: 10,
                    }}
                  >
                    {t.fields.recommendedWindows}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
                    {vivahData.recommended_windows.map((w, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 14,
                          background: SHELL_COLORS_INDIA_V2.section,
                          border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
                        }}
                      >
                        <div style={{ fontSize: 14.5, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}>
                          {w.window_label}
                        </div>
                        <div style={{ fontSize: 13, color: "#4A3B5C", lineHeight: 1.6 }}>
                          {w.feeling}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <AstriaIndiaV2Divider />
              <AstriaIndiaV2FieldBlock
                label={t.fields.timingToApproachGently}
                value={vivahData.timing_to_approach_gently}
              />
              {vivahData.closing && (
                <p style={{ fontSize: 14, fontStyle: "italic", color: "#4A3B5C", marginTop: 8 }}>
                  {vivahData.closing}
                </p>
              )}
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !vivahData && message && (
          <AstriaIndiaV2ResultCard
            icon={<Clock className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#C9A24B", "#6B3FA0"]}
          >
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", whiteSpace: "pre-wrap" }}>
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="vivah"
      lang={lang}
      onLangChange={setLang}
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#C9A24B"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep
          stepKey="details"
          question={t.step1Question}
          helper={t.step1Helper}
        >
          <PersonDetailsGroup label={t.brideLabel} value={bride} onChange={setBride} copy={t} />
          <PersonDetailsGroup label={t.groomLabel} value={groom} onChange={setGroom} copy={t} />
          <WizardNav
            showBack={false}
            nextDisabled={!step1Valid}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="your-readiness" question={t.step2Question}>
          <WizardSlider value={yourReadiness} onChange={setYourReadiness} lowLabel={t.readinessLow} highLabel={t.readinessHigh} />
          <WizardNav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep stepKey="their-readiness" question={t.step3Question}>
          <WizardSlider value={theirReadiness} onChange={setTheirReadiness} lowLabel={t.readinessLow} highLabel={t.readinessHigh} />
          <WizardNav
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 4 && (
        <WizardStep stepKey="rhythm" question={t.step4Question}>
          <WizardChips options={t.rhythmOptions} value={rhythm} onChange={setRhythm} />
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: SHELL_COLORS_INDIA_V2.mutedColor,
                marginBottom: 8,
              }}
            >
              {t.preferredTimingLabel}
            </div>
            <WizardDropdown
              options={t.periodOptions}
              value={period}
              onChange={setPeriod}
              placeholder={t.periodPlaceholder}
            />
          </div>
          <WizardNav
            onBack={() => setStep(3)}
            nextLabel={t.revealButton}
            nextDisabled={!rhythm || !period}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default VivahMuhuratTab;
